import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { DollarSign, Phone, Clock, User, Mail, FileText, AlertCircle, Upload, X, Eye, Download } from 'lucide-react';
import { 
  EntryType, 
  IncludeOnInvoiceMetadata, 
  CallMetadata, 
  StatusUpdateMetadata,
  VendorMessageMetadata,
  ClientMessageMetadata,
  FileUploadMetadata,
  EmailMetadata,
  SystemMessageMetadata,
  VerbalAuthorizationMetadata
} from '../../types';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import clsx from 'clsx';
import { JOB_ATTACHMENT_ACCEPT, JOB_ATTACHMENT_HELP_CONTENT, JOB_ATTACHMENT_HELP_TITLE, takeAllowedAttachments } from '../../utils/jobAttachments';
import HelpTooltip from '../ui/HelpTooltip';

interface TimelineEntryFormProps {
  entryTypes: EntryType[];
  onSubmit: (entryTypeId: number, metadata: any, message: string, attachments?: File[]) => void | Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  uploadProgress?: number | null;
  isModal?: boolean; // New prop to indicate if this is inside a modal
  currentStatusName?: string; // For pre-filling previous status
  currentStatusId?: number; // For id-based previous status
  vendors?: Array<{ vendorId: number; vendorName: string }>; // Vendors from JCS
  defaultVendorId?: number; // Optional preselected vendor
  // Client contact info from JCS
  clientContactName?: string;
  clientContactPhone?: string;
  clientContactEmail?: string;
  // Controlled entry type (e.g. when opened from Files tab to default to File Upload)
  entryTypeId?: number;
  setEntryTypeId?: (id: number) => void;
}

const TimelineEntryForm: React.FC<TimelineEntryFormProps> = ({
  entryTypes,
  onSubmit,
  onCancel,
  isLoading = false,
  uploadProgress = null,
  isModal = false,
  currentStatusName,
  currentStatusId,
  vendors = [],
  defaultVendorId,
  clientContactName,
  clientContactPhone,
  clientContactEmail,
  entryTypeId: controlledEntryTypeId,
  setEntryTypeId: setControlledEntryTypeId,
}) => {
  const { user } = useAuth();
  const [internalEntryTypeId, setInternalEntryTypeId] = useState<number>(1); // Default to Note
  const selectedEntryTypeId = controlledEntryTypeId ?? internalEntryTypeId;
  const setSelectedEntryTypeId = setControlledEntryTypeId ?? setInternalEntryTypeId;
  const [message, setMessage] = useState('');
  const [metadata, setMetadata] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const isBusy = isLoading || isSubmitting;

  const entryType = entryTypes.find(et => et.entryTypeId === selectedEntryTypeId);

  // Fetch statuses for Status Update dropdowns
  const { data: statusesResponse } = useQuery({
    queryKey: ['statuses'],
    queryFn: () => apiService.getStatuses()
  });
  const statuses = statusesResponse?.data || [];

  useEffect(() => {
    // Initialize metadata based on entry type
    switch (selectedEntryTypeId) {
      case 5: // Include on Invoice
        setMetadata({
          description: '',
          quantity: 1,
          rate: 0,
          isTaxable: true,
          unit: 'each'
        });
        break;
      case 2: // Call
        setMetadata({
          phoneNumber: '',
          durationMinutes: 0,
          callDirection: 'Outbound',
          callType: 'General'
        });
        break;
      case 6: // Status Update
        setMetadata({
          previousStatus: currentStatusName || '',
          previousStatusId: currentStatusId ?? undefined,
          newStatus: '',
          newStatusId: undefined,
          reason: '',
          updatedBy: user ? `${user.firstName} ${user.lastName}`.trim() : ''
        });
        break;
      case 7: // Vendor Message
        // Auto-populate from JCS vendors; non-editable in UI
        const resolvedVendor = (() => {
          if (defaultVendorId) return vendors.find(v => v.vendorId === defaultVendorId);
          if (vendors.length === 1) return vendors[0];
          return undefined;
        })();
        setMetadata({
          vendorId: resolvedVendor?.vendorId,
          vendorName: resolvedVendor?.vendorName,
          messageType: 'General',
          priority: 'Normal',
          responseRequired: false
        });
        break;
      case 8: // Client Message
        setMetadata({
          contactPerson: clientContactName || '',
          contactPhone: clientContactPhone ? formatPhoneNumber(clientContactPhone) : '',
          contactEmail: clientContactEmail || '',
          messageType: 'General',
          priority: 'Normal',
          responseRequired: false
        });
        console.log('Client Message metadata initialized:', {
          contactPerson: clientContactName || '',
          contactPhone: clientContactPhone || '',
          contactEmail: clientContactEmail || ''
        });
        break;
      case 4: // File Upload
        setMetadata({
          fileType: 'document',
          isConfidential: false
        });
        break;
      case 3: // Email
        setMetadata({
          emailType: 'General',
          isOutbound: true,
          hasAttachments: false
        });
        break;
      case 9: // System Message
        setMetadata({
          messageType: 'General',
          isUserVisible: true
        });
        break;
      case 10: // Verbal Authorization
        {
          const resolvedVendor = (() => {
            if (defaultVendorId) return vendors.find(v => v.vendorId === defaultVendorId);
            if (vendors.length === 1) return vendors[0];
            return undefined;
          })();
          setMetadata({
            vendorId: resolvedVendor?.vendorId,
            vendorName: resolvedVendor?.vendorName,
            amountAuthorized: 0,
            authorizationMethod: 'Phone',
            notes: ''
          });
        }
        break;
      default:
        setMetadata({});
    }
  }, [selectedEntryTypeId]);

  // Keep previousStatus in sync if work order status changes while form is on Status Update
  useEffect(() => {
    if (selectedEntryTypeId === 6) {
      setMetadata((prev: any) => ({ ...prev, previousStatus: currentStatusName || '', previousStatusId: currentStatusId ?? prev?.previousStatusId }));
    }
  }, [currentStatusName, currentStatusId, selectedEntryTypeId]);

  // Ensure drag-and-drop area and selected files are ONLY active for File Upload
  useEffect(() => {
    if (selectedEntryTypeId !== 4 && attachedFiles.length > 0) {
      setAttachedFiles([]);
    }
  }, [selectedEntryTypeId]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!message.trim()) {
      newErrors.message = 'Message is required';
    }

    // Entry type specific validation
    switch (selectedEntryTypeId) {
      case 5: // Include on Invoice
        if (!metadata.description?.trim()) {
          newErrors.description = 'Description is required';
        }
        if (!metadata.quantity || metadata.quantity <= 0) {
          newErrors.quantity = 'Quantity must be greater than 0';
        }
        if (!metadata.rate || metadata.rate < 0) {
          newErrors.rate = 'Rate must be 0 or greater';
        }
        break;
      case 2: // Call
        if (!metadata.phoneNumber?.trim()) {
          newErrors.phoneNumber = 'Phone number is required';
        }
        if (metadata.durationMinutes < 0) {
          newErrors.durationMinutes = 'Duration cannot be negative';
        }
        break;
      case 6: // Status Update
        if (!metadata.newStatusId || Number.isNaN(metadata.newStatusId)) {
          newErrors.newStatus = 'New status is required';
        }
        break;
      case 4: // File Upload
        if (attachedFiles.length === 0) {
          newErrors.files = 'At least one file must be uploaded';
        }
        break;
      case 7: // Vendor Message
        if (!metadata.vendorId) {
          newErrors.vendorId = 'Vendor is required (auto-filled from Job Contact Sheet)';
        }
        break;
      case 10: // Verbal Authorization
        if (!metadata.vendorId) {
          newErrors.vendorId = 'Vendor is required';
        }
        if (!metadata.amountAuthorized || metadata.amountAuthorized <= 0) {
          newErrors.amountAuthorized = 'Authorized amount must be greater than 0';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current || isBusy) {
      return;
    }
    if (!validateForm()) {
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);
    const normalizedMetadata = selectedEntryTypeId === 5 ? { ...metadata, unit: 'each' } : metadata;
    try {
      await onSubmit(
        selectedEntryTypeId,
        normalizedMetadata,
        message,
        attachedFiles.length > 0 ? attachedFiles : undefined
      );
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const updateMetadata = (key: string, value: any) => {
    setMetadata(prev => ({ ...prev, [key]: value }));
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: '' }));
    }
  };

  // File upload handlers
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = takeAllowedAttachments(Array.from(event.target.files || []));
    if (files.length > 0) {
      setAttachedFiles(prev => [...prev, ...files]);
    }
    event.target.value = '';
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Drag and drop handlers
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('TimelineEntryForm handleDragOver called');
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('TimelineEntryForm handleDragEnter called');
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('TimelineEntryForm handleDragLeave called');
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    console.log('TimelineEntryForm handleDrop called');
    console.log('event.dataTransfer:', event.dataTransfer);
    console.log('event.dataTransfer.files:', event.dataTransfer.files);
    console.log('event.dataTransfer.files.length:', event.dataTransfer.files.length);
    
    const files = takeAllowedAttachments(Array.from(event.dataTransfer.files));
    if (files.length > 0) {
      setAttachedFiles(prev => [...prev, ...files]);
    }
  };

  // Helper function to check if file is an image
  const isImageFile = (fileName: string): boolean => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  };

  // Helper to choose icon by file extension
  const getFileTypeIcon = (fileName: string) => {
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    switch (extension) {
      case '.pdf':
        return { Icon: FileText, className: 'text-red-500' };
      case '.doc':
      case '.docx':
        return { Icon: FileText, className: 'text-blue-500' };
      case '.xls':
      case '.xlsx':
        return { Icon: FileText, className: 'text-green-500' };
      case '.jpg':
      case '.jpeg':
      case '.png':
      case '.gif':
      case '.bmp':
      case '.webp':
      case '.svg':
        return { Icon: Eye, className: 'text-purple-500' };
      default:
        return { Icon: FileText, className: 'text-gray-500' };
    }
  };

  // Phone number formatting function
  const formatPhoneNumber = (value: string) => {
    // Remove all non-numeric characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (phoneNumber.length >= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    } else if (phoneNumber.length >= 3) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumber.length > 0) {
      return `(${phoneNumber}`;
    }
    return phoneNumber;
  };

  const renderMetadataFields = () => {
    switch (selectedEntryTypeId) {
      case 5: // Include on Invoice
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="description">Description *</Label>
                <Input
                  id="description"
                  value={metadata.description || ''}
                  onChange={(e) => updateMetadata('description', e.target.value)}
                  placeholder="Item description"
                  className={errors.description ? 'border-red-500' : ''}
                />
                {errors.description && <p className="text-sm text-red-500 mt-1">{errors.description}</p>}
              </div>
              <div>
                <Label htmlFor="unit">Unit</Label>
                <Select
                  value={(metadata.unit || 'each').toString()}
                  onValueChange={(value) => updateMetadata('unit', value)}
                >
                  <SelectTrigger id="unit">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="each">each</SelectItem>
                    <SelectItem value="hour">hour</SelectItem>
                    <SelectItem value="day">day</SelectItem>
                    <SelectItem value="week">week</SelectItem>
                    <SelectItem value="month">month</SelectItem>
                    <SelectItem value="service">service</SelectItem>
                    <SelectItem value="trip">trip</SelectItem>
                    <SelectItem value="mile">mile</SelectItem>
                    <SelectItem value="foot">foot</SelectItem>
                    <SelectItem value="square foot">square foot</SelectItem>
                    <SelectItem value="cubic yard">cubic yard</SelectItem>
                    <SelectItem value="pound">pound</SelectItem>
                    <SelectItem value="gallon">gallon</SelectItem>
                    <SelectItem value="package">package</SelectItem>
                    <SelectItem value="set">set</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="quantity">Quantity *</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={metadata.quantity || 1}
                  onChange={(e) => updateMetadata('quantity', parseFloat(e.target.value) || 0)}
                  className={errors.quantity ? 'border-red-500' : ''}
                />
                {errors.quantity && <p className="text-sm text-red-500 mt-1">{errors.quantity}</p>}
              </div>
              <div>
                <Label htmlFor="rate">Rate ($) *</Label>
                <Input
                  id="rate"
                  type="number"
                  min="0"
                  step="0.01"
                  value={metadata.rate || 0}
                  onChange={(e) => updateMetadata('rate', parseFloat(e.target.value) || 0)}
                  className={errors.rate ? 'border-red-500' : ''}
                />
                {errors.rate && <p className="text-sm text-red-500 mt-1">{errors.rate}</p>}
              </div>
              <div>
                <Label>Total</Label>
                <div className="flex items-center h-10 px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  <DollarSign className="h-4 w-4 mr-2 text-gray-500" />
                  <span className="font-medium">
                    ${((metadata.quantity || 0) * (metadata.rate || 0)).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isTaxable"
                checked={metadata.isTaxable || false}
                onChange={(e) => updateMetadata('isTaxable', e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="isTaxable">Taxable</Label>
            </div>
          </div>
        );

      case 2: // Call
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phoneNumber">Phone Number *</Label>
                <Input
                  id="phoneNumber"
                  value={metadata.phoneNumber || ''}
                  onChange={(e) => updateMetadata('phoneNumber', formatPhoneNumber(e.target.value))}
                  placeholder="(555) 123-4567"
                  className={errors.phoneNumber ? 'border-red-500' : ''}
                />
                {errors.phoneNumber && <p className="text-sm text-red-500 mt-1">{errors.phoneNumber}</p>}
              </div>
              <div>
                <Label htmlFor="contactPerson">Contact Person</Label>
                <Input
                  id="contactPerson"
                  value={metadata.contactPerson || ''}
                  onChange={(e) => updateMetadata('contactPerson', e.target.value)}
                  placeholder="Person's name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="durationMinutes">Duration (minutes)</Label>
                <Input
                  id="durationMinutes"
                  type="number"
                  min="0"
                  value={metadata.durationMinutes || 0}
                  onChange={(e) => updateMetadata('durationMinutes', parseInt(e.target.value) || 0)}
                  className={errors.durationMinutes ? 'border-red-500' : ''}
                />
                {errors.durationMinutes && <p className="text-sm text-red-500 mt-1">{errors.durationMinutes}</p>}
              </div>
              <div>
                <Label htmlFor="callDirection">Call Direction</Label>
                <Select value={metadata.callDirection || 'Outbound'} onValueChange={(value) => updateMetadata('callDirection', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Inbound">Inbound</SelectItem>
                    <SelectItem value="Outbound">Outbound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="callType">Call Type</Label>
              <Select value={metadata.callType || 'General'} onValueChange={(value) => updateMetadata('callType', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="General">General</SelectItem>
                  <SelectItem value="Emergency">Emergency</SelectItem>
                  <SelectItem value="Follow-up">Follow-up</SelectItem>
                  <SelectItem value="Quote Request">Quote Request</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 6: // Status Update
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="previousStatus">Previous Status</Label>
                <Select value={metadata.previousStatus || ''} onValueChange={(value) => updateMetadata('previousStatus', value)} disabled>
                  <SelectTrigger id="previousStatus" disabled>
                    <SelectValue placeholder="Select previous status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((s: any) => (
                      <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="newStatus">New Status *</Label>
                <Select
                  value={(metadata.newStatusId || '').toString()}
                  onValueChange={(value) => {
                    const id = parseInt(value);
                    const s = statuses.find((x: any) => x.id === id);
                    // Store both ID and name for backward compatibility with backend
                    setMetadata((prev: any) => ({
                      ...prev,
                      newStatusId: id,
                      newStatus: s ? s.name : prev?.newStatus || ''
                    }));
                    // Clear validation error if any
                    if (errors.newStatus) {
                      setErrors((e) => ({ ...e, newStatus: '' }));
                    }
                  }}
                >
                  <SelectTrigger id="newStatus" className={errors.newStatus ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select new status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map((s: any) => (
                      <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.newStatus && <p className="text-sm text-red-500 mt-1">{errors.newStatus}</p>}
              </div>
            </div>
            <div>
              <Label htmlFor="reason">Reason for Change</Label>
              <Textarea
                id="reason"
                value={metadata.reason || ''}
                onChange={(e) => updateMetadata('reason', e.target.value)}
                placeholder="Reason for status change"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="updatedBy">Updated By</Label>
              <Input
                id="updatedBy"
                value={metadata.updatedBy || (user ? `${user.firstName} ${user.lastName}`.trim() : '')}
                readOnly
              />
            </div>
          </div>
        );

      case 7: // Vendor Message
        return (
          <div className="space-y-4">
            {/* Keep vendorId in metadata, but hide the ID field from UI */}
            <div>
              <Label htmlFor="vendorName">Vendor</Label>
              <Input
                id="vendorName"
                value={metadata.vendorName || ''}
                readOnly
                placeholder="Vendor name"
              />
              {errors.vendorId && <p className="text-sm text-red-500 mt-1">{errors.vendorId}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="messageType">Message Type</Label>
                <Select value={metadata.messageType || 'General'} onValueChange={(value) => updateMetadata('messageType', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General">General</SelectItem>
                    <SelectItem value="Quote">Quote</SelectItem>
                    <SelectItem value="Update">Update</SelectItem>
                    <SelectItem value="Issue">Issue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="priority">Priority</Label>
                <Select value={metadata.priority || 'Normal'} onValueChange={(value) => updateMetadata('priority', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Normal">Normal</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="contactPerson">Contact Person</Label>
              <Input
                id="contactPerson"
                value={metadata.contactPerson || ''}
                onChange={(e) => updateMetadata('contactPerson', e.target.value)}
                placeholder="Contact person name"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="responseRequired"
                checked={metadata.responseRequired || false}
                onChange={(e) => updateMetadata('responseRequired', e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="responseRequired">Response Required</Label>
            </div>
          </div>
        );

      case 10: // Verbal Authorization
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="verbalVendorName">Vendor</Label>
              <Input id="verbalVendorName" value={metadata.vendorName || ''} readOnly />
              {errors.vendorId && <p className="text-sm text-red-500 mt-1">{errors.vendorId}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="amountAuthorized">Amount Authorized</Label>
                <Input
                  id="amountAuthorized"
                  type="number"
                  min="0"
                  step="0.01"
                  value={metadata.amountAuthorized ?? ''}
                  onChange={(e) => updateMetadata('amountAuthorized', parseFloat(e.target.value) || 0)}
                />
                {errors.amountAuthorized && <p className="text-sm text-red-500 mt-1">{errors.amountAuthorized}</p>}
              </div>
              <div>
                <Label htmlFor="authorizationMethod">Method</Label>
                <Select
                  value={metadata.authorizationMethod || 'Phone'}
                  onValueChange={(value) => updateMetadata('authorizationMethod', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Phone">Phone</SelectItem>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="Text">Text</SelectItem>
                    <SelectItem value="In Person">In Person</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="verbalNotes">Notes</Label>
              <Textarea
                id="verbalNotes"
                value={metadata.notes || ''}
                onChange={(e) => updateMetadata('notes', e.target.value)}
                rows={2}
                placeholder="Who was spoken with, context, etc."
              />
            </div>
          </div>
        );

      case 4: // File Upload
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="fileDescription">File Description</Label>
              <Textarea
                id="fileDescription"
                value={metadata.description || ''}
                onChange={(e) => updateMetadata('description', e.target.value)}
                rows={3}
                placeholder="Describe the files being uploaded..."
              />
            </div>
            
            {/* File Upload Area */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Label>Upload Files</Label>
                <HelpTooltip
                  title={JOB_ATTACHMENT_HELP_TITLE}
                  content={JOB_ATTACHMENT_HELP_CONTENT}
                  position="bottom"
                />
              </div>
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center"
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 mb-2">Drop files here or click to upload</p>
                <p className="text-xs text-gray-500 mb-3">Photos, PDF, Word, Excel, and video up to 50 MB</p>
                <input
                  type="file"
                  multiple
                  accept={JOB_ATTACHMENT_ACCEPT}
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('file-upload')?.click()}
                  className="text-sm"
                >
                  Choose Files
                </Button>
              </div>
              
              {/* File List */}
              {attachedFiles.length > 0 && (
                <div className="space-y-2">
                  <Label>Selected Files</Label>
                  <div className="max-h-32 overflow-y-auto space-y-2">
                    {attachedFiles.map((file, index) => {
                      const { Icon, className } = getFileTypeIcon(file.name);
                      return (
                        <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-2 rounded-lg">
                          <div className="flex items-center space-x-2 min-w-0 flex-1">
                            <Icon className={`h-4 w-4 ${className} flex-shrink-0`} />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {file.name}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {(file.size / 1024).toFixed(1)} KB
                              </div>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {errors.files && <p className="text-sm text-red-500 mt-1">{errors.files}</p>}
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isConfidential"
                checked={metadata.isConfidential || false}
                onChange={(e) => updateMetadata('isConfidential', e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="isConfidential">Confidential File</Label>
            </div>
          </div>
        );

      case 8: // Client Message
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contactPerson">Contact Person</Label>
                <Input
                  id="contactPerson"
                  value={metadata.contactPerson || ''}
                  onChange={(e) => updateMetadata('contactPerson', e.target.value)}
                  placeholder="Contact person name"
                />
              </div>
              <div>
                <Label htmlFor="contactEmail">Contact Email</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  value={metadata.contactEmail || ''}
                  onChange={(e) => updateMetadata('contactEmail', e.target.value)}
                  placeholder="email@example.com"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="contactPhone">Contact Phone</Label>
              <Input
                id="contactPhone"
                value={metadata.contactPhone || ''}
                onChange={(e) => updateMetadata('contactPhone', formatPhoneNumber(e.target.value))}
                placeholder="(555) 123-4567"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="messageType">Message Type</Label>
                <Select value={metadata.messageType || 'General'} onValueChange={(value) => updateMetadata('messageType', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General">General</SelectItem>
                    <SelectItem value="Question">Question</SelectItem>
                    <SelectItem value="Complaint">Complaint</SelectItem>
                    <SelectItem value="Praise">Praise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="priority">Priority</Label>
                <Select value={metadata.priority || 'Normal'} onValueChange={(value) => updateMetadata('priority', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Normal">Normal</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="responseRequired"
                checked={metadata.responseRequired || false}
                onChange={(e) => updateMetadata('responseRequired', e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="responseRequired">Response Required</Label>
            </div>
          </div>
        );

      case 9: // System Message
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="messageType">Message Type</Label>
              <Select value={metadata.messageType || 'General'} onValueChange={(value) => updateMetadata('messageType', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="General">General</SelectItem>
                  <SelectItem value="AutoStatusChange">Auto Status Change</SelectItem>
                  <SelectItem value="Reminder">Reminder</SelectItem>
                  <SelectItem value="Alert">Alert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="triggeredBy">Triggered By</Label>
              <Input
                id="triggeredBy"
                value={metadata.triggeredBy || ''}
                onChange={(e) => updateMetadata('triggeredBy', e.target.value)}
                placeholder="System, User, etc."
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isUserVisible"
                checked={metadata.isUserVisible !== false}
                onChange={(e) => updateMetadata('isUserVisible', e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="isUserVisible">Visible to Users</Label>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center text-gray-500 py-8">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>No additional fields required for this entry type.</p>
          </div>
        );
    }
  };

  const getEntryTypeIcon = () => {
    switch (selectedEntryTypeId) {
      case 5: return <DollarSign className="h-5 w-5" />;
      case 2: return <Phone className="h-5 w-5" />;
      case 6: return <Clock className="h-5 w-5" />;
      case 7: case 8: return <User className="h-5 w-5" />;
      case 3: return <Mail className="h-5 w-5" />;
      case 4: return <FileText className="h-5 w-5" />;
      default: return <AlertCircle className="h-5 w-5" />;
    }
  };

  return (
    <div className="relative w-full space-y-6">
      {isBusy && (
        <div
          className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-lg bg-white/90 dark:bg-gray-900/90 px-6"
          role="status"
          aria-live="polite"
          aria-label={uploadProgress != null ? `Uploading ${uploadProgress} percent` : 'Saving timeline entry'}
        >
          <div className="w-full max-w-sm space-y-3 text-center">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {attachedFiles.length > 0 ? 'Uploading files…' : 'Saving entry…'}
            </p>
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className={clsx(
                  'h-full rounded-full bg-accent-color transition-all duration-200',
                  uploadProgress == null && 'w-2/5 animate-pulse'
                )}
                style={uploadProgress != null ? { width: `${uploadProgress}%` } : undefined}
              />
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {uploadProgress != null
                ? `${uploadProgress}% — please wait, do not click Add again`
                : 'Please wait, do not click Add again'}
            </p>
          </div>
        </div>
      )}
      {!isModal && (
        <CardHeader className="p-0 pb-4">
          <CardTitle className="flex items-center space-x-2 text-sm">
            {getEntryTypeIcon()}
            <span>Add {entryType?.name || 'Entry'}</span>
          </CardTitle>
        </CardHeader>
      )}
      
      <div className={isModal ? "w-full space-y-6" : ""}>
        <form onSubmit={handleSubmit} className="w-full space-y-6" aria-busy={isBusy}>
          <fieldset disabled={isBusy} className="w-full space-y-6 min-w-0 border-0 p-0 m-0">
          {/* Entry Type Selector */}
          <div className="w-full">
            <Label htmlFor="entryType">Entry Type</Label>
            <Select value={selectedEntryTypeId.toString()} onValueChange={(value) => setSelectedEntryTypeId(parseInt(value, 10))}>
              <SelectTrigger id="entryType">
                <SelectValue placeholder="Select entry type" />
              </SelectTrigger>
              <SelectContent>
                {entryTypes.map((entryType) => (
                  <SelectItem key={entryType.entryTypeId} value={entryType.entryTypeId.toString()}>
                    {entryType.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Message Field */}
          <div className="w-full">
            <Label htmlFor="message">Message *</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your message..."
              rows={4}
              className={errors.message ? 'border-red-500' : ''}
            />
            {errors.message && <p className="text-sm text-red-500 mt-1">{errors.message}</p>}
          </div>

          {/* Entry Type Specific Fields */}
          {renderMetadataFields()}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isBusy}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isBusy}
              className="bg-accent-color hover:bg-accent-color/90"
            >
              {isBusy
                ? attachedFiles.length > 0
                  ? uploadProgress != null
                    ? `Uploading ${uploadProgress}%`
                    : 'Uploading...'
                  : 'Adding...'
                : 'Add Entry'}
            </Button>
          </div>
        </fieldset>
        </form>
      </div>
    </div>
  );
};

export default TimelineEntryForm;
